import { Link } from 'react-router-dom';
import { Award, BookOpen, CheckCircle, MapPin } from 'lucide-react';

// 10 Jewelry Expert Profiles with Photos
const EXPERTS = {
  'Isabella Chen': {
    name: 'Isabella Chen',
    title: 'Founder & Lead Authenticator',
    credentials: 'GIA Graduate Gemologist',
    photo: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=400&h=400&fit=crop&crop=face',
    bio: 'Isabella spent 12 years as a senior specialist at a major auction house before founding Jewelry Refined. She has personally authenticated over 3,000 pieces from luxury houses and holds certifications from the Gemological Institute of America (GIA).',
    expertise: ['Van Cleef & Arpels', 'Cartier', 'Bulgari', 'High Jewelry'],
    experience: '12+ years',
    location: 'New York, NY'
  },
  'James Thornton': {
    name: 'James Thornton',
    title: 'Senior Editor & Market Analyst',
    credentials: 'Luxury Market Expert',
    photo: 'https://images.unsplash.com/photo-1651684215020-f7a5b6610f23?w=400&h=400&fit=crop&crop=face',
    bio: 'James brings 8 years of experience tracking luxury resale markets. His market analysis has been featured in leading jewelry trade publications and he specializes in price trends and auction markets.',
    expertise: ['Market Trends', 'Price Analysis', 'Auction Markets', 'Investment'],
    experience: '8+ years',
    location: 'London, UK'
  },
  'Natalie Brooks': {
    name: 'Natalie Brooks',
    title: 'Authentication Specialist',
    credentials: 'GIA Pearls Graduate',
    photo: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=400&h=400&fit=crop&crop=face',
    bio: 'Natalie is a certified pearl specialist with 10 years of hands-on experience at major resale platforms. She specializes in vintage jewelry authentication and condition assessment.',
    expertise: ['Pearls', 'Vintage Jewelry', 'Condition Grading', 'Authentication'],
    experience: '10+ years',
    location: 'Los Angeles, CA'
  },
  'David Martinez': {
    name: 'David Martinez',
    title: 'Contributing Writer',
    credentials: 'Jewelry Collector & Enthusiast',
    photo: 'https://images.unsplash.com/photo-1576558656222-ba66febe3dec?w=400&h=400&fit=crop&crop=face',
    bio: 'David is an avid jewelry collector and writer who has spent over $100,000 on pre-owned luxury pieces. He shares his personal buying experiences and hard-earned lessons with our readers.',
    expertise: ['Buying Guides', 'Personal Experience', 'Estate Sales', 'Budget Luxury'],
    experience: '6+ years',
    location: 'Miami, FL'
  },
  'Sophie Laurent': {
    name: 'Sophie Laurent',
    title: 'European Correspondent',
    credentials: 'Former Brand Specialist',
    photo: 'https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?w=400&h=400&fit=crop&crop=face',
    bio: 'Sophie worked for 7 years at luxury brand boutiques in Paris before joining Jewelry Refined. She brings insider knowledge of how luxury houses operate and authenticate pieces.',
    expertise: ['Brand Authentication', 'European Markets', 'Chopard', 'Piaget'],
    experience: '7+ years',
    location: 'Paris, France'
  },
  'Michael Chen': {
    name: 'Michael Chen',
    title: 'Diamond Specialist',
    credentials: 'GIA Graduate Diamonds',
    photo: 'https://images.unsplash.com/photo-1769636929261-e913ed023c83?w=400&h=400&fit=crop&crop=face',
    bio: 'Michael has spent 15 years in the diamond trade, working with both natural and lab-grown stones. He provides expert guidance on diamond quality, pricing, and investment potential.',
    expertise: ['Diamonds', 'Gemstone Grading', 'Harry Winston', 'Graff'],
    experience: '15+ years',
    location: 'Antwerp, Belgium'
  },
  'Elena Rossi': {
    name: 'Elena Rossi',
    title: 'Vintage Jewelry Historian',
    credentials: 'Art History PhD',
    photo: 'https://images.unsplash.com/photo-1769636930016-5d9f0ca653aa?w=400&h=400&fit=crop&crop=face',
    bio: 'Elena holds a PhD in Art History with a focus on decorative arts. She specializes in dating and authenticating antique jewelry from the Georgian era through Art Deco.',
    expertise: ['Antique Jewelry', 'Art Deco', 'Victorian Era', 'Jewelry History'],
    experience: '12+ years',
    location: 'Florence, Italy'
  },
  'Robert Kim': {
    name: 'Robert Kim',
    title: 'Watch & Jewelry Appraiser',
    credentials: 'ASA Certified Appraiser',
    photo: 'https://images.unsplash.com/photo-1769636930047-4478f12cf430?w=400&h=400&fit=crop&crop=face',
    bio: 'Robert is a certified appraiser with the American Society of Appraisers. He has valued over $50 million in jewelry and watches for insurance, estate, and resale purposes.',
    expertise: ['Appraisals', 'Insurance', 'Watches', 'Estate Valuation'],
    experience: '18+ years',
    location: 'Chicago, IL'
  },
  'Amanda Foster': {
    name: 'Amanda Foster',
    title: 'Styling & Trends Editor',
    credentials: 'Former Vogue Contributor',
    photo: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&w=400&h=400&fit=crop',
    bio: 'Amanda spent 5 years as a jewelry editor at major fashion publications. She now helps readers style their collections and stay ahead of jewelry trends.',
    expertise: ['Jewelry Styling', 'Trends', 'Layering', 'Fashion'],
    experience: '8+ years',
    location: 'New York, NY'
  },
  'Thomas Wright': {
    name: 'Thomas Wright',
    title: 'Platform Review Specialist',
    credentials: 'E-Commerce Expert',
    photo: 'https://images.pexels.com/photos/14589344/pexels-photo-14589344.jpeg?auto=compress&w=400&h=400&fit=crop',
    bio: 'Thomas has personally tested over 30 luxury resale platforms, spending thousands on test purchases. He provides unbiased reviews and platform comparisons.',
    expertise: ['Platform Reviews', 'Fashionphile', 'The RealReal', '1stDibs'],
    experience: '5+ years',
    location: 'San Francisco, CA'
  }
};

// Compact author byline for article headers
export const AuthorByline = ({ authorName, authorTitle, publishedAt }) => {
  const expert = EXPERTS[authorName] || {
    name: authorName || 'Jewelry Refined Team',
    title: authorTitle || 'Luxury Jewelry Expert',
    credentials: authorTitle || 'Expert',
    photo: null,
    location: 'New York, NY'
  };

  return (
    <div className="flex items-center justify-center gap-4" data-testid="author-byline">
      <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-200 flex-shrink-0">
        {expert.photo ? (
          <img 
            src={expert.photo} 
            alt={expert.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-amber-100 flex items-center justify-center">
            <span className="text-xl font-medium text-amber-800" style={{fontFamily: "'Playfair Display', serif"}}>
              {expert.name.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
        )}
      </div>
      <div className="text-left">
        <p className="font-medium text-stone-900">{expert.name}</p>
        <p className="text-sm text-amber-800">{expert.credentials}</p>
        {publishedAt && (
          <p className="text-xs text-stone-500 mt-0.5">
            {new Date(publishedAt).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        )}
      </div>
    </div>
  );
};

// Full author bio box for end of articles (E-E-A-T best practice)
export const AuthorBioBox = ({ authorName }) => {
  const expert = EXPERTS[authorName];
  
  if (!expert) {
    return null;
  }

  return (
    <div className="bg-stone-50 border border-stone-200 p-6 md:p-8" data-testid="author-bio-box">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Author Photo */}
        <div className="flex-shrink-0">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-amber-200 mx-auto md:mx-0">
            {expert.photo ? (
              <img 
                src={expert.photo} 
                alt={expert.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-amber-100 flex items-center justify-center">
                <span className="text-3xl font-medium text-amber-800" style={{fontFamily: "'Playfair Display', serif"}}>
                  {expert.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Author Info */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
            <h4 className="font-medium text-lg text-stone-900" style={{fontFamily: "'Playfair Display', serif"}}>
              {expert.name}
            </h4>
            <div className="flex items-center justify-center md:justify-start gap-2">
              <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                <CheckCircle className="w-3 h-3" />
                Verified Expert
              </span>
            </div>
          </div>
          
          <p className="text-amber-800 text-sm mb-1">{expert.title} · {expert.credentials}</p>
          <p className="text-stone-500 text-xs mb-3 flex items-center justify-center md:justify-start gap-1">
            <MapPin className="w-3 h-3" /> {expert.location}
          </p>
          
          <p className="text-stone-600 text-sm mb-4 leading-relaxed">
            {expert.bio}
          </p>

          {/* Credentials & Expertise */}
          <div className="flex flex-wrap gap-4 justify-center md:justify-start text-xs text-stone-500">
            <span className="flex items-center gap-1">
              <Award className="w-4 h-4 text-amber-700" />
              {expert.experience} experience
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4 text-amber-700" />
              {expert.expertise.slice(0, 2).join(', ')}
            </span>
          </div>

          {/* Expertise Tags */}
          <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
            {expert.expertise.map((skill, index) => (
              <span 
                key={index}
                className="text-xs bg-white border border-stone-200 text-stone-600 px-2 py-1"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Trust Footer */}
      <div className="mt-6 pt-4 border-t border-stone-200">
        <p className="text-xs text-stone-500 text-center md:text-left">
          All content is reviewed by our editorial team for accuracy. 
          <Link to="/about" className="text-amber-800 hover:underline ml-1">
            Meet all our experts →
          </Link>
        </p>
      </div>
    </div>
  );
};

// Export experts for use in other components
export const getAllExperts = () => Object.values(EXPERTS);
export const getExpertByName = (name) => EXPERTS[name];

export default AuthorBioBox;
